import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Wrench, Loader2, Sun, LogIn, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_f9f64a4c-3451-4815-8703-6feb7c894858/artifacts/mema1897_Updated%20Logo.png';
const LOGO_WHITE_URL = 'https://customer-assets.emergentagent.com/job_electrical-tracker-1/artifacts/8ty3m189_Updated%20Logo%20White.png';

export const EngineerLogin = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // login | register
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'login') {
        const res = await api.post('/engineer-auth/login', { email: form.email, password: form.password });
        localStorage.setItem('engineer_token', res.data.token);
        localStorage.setItem('engineer_user', JSON.stringify(res.data.user));
        toast.success('Welcome back!');
        navigate('/engineer-app');
      } else {
        await api.post('/engineer-auth/register', form);
        toast.success('Registration submitted! Awaiting admin approval.');
        setMode('login');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const isDark = document.documentElement.classList.contains('dark');

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4" data-testid="engineer-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src={isDark ? LOGO_WHITE_URL : LOGO_URL}
            alt="Helios"
            className="h-14 mx-auto mb-4"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <div className="flex items-center justify-center gap-2 mb-2">
            <Wrench className="w-6 h-6 text-blue-600" />
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white font-['Manrope']">Engineer Portal</h1>
          </div>
          <p className="text-slate-500 dark:text-slate-400 text-sm">Access your installations and job details</p>
        </div>

        <Card className="border-slate-200 dark:border-slate-700" data-testid="engineer-auth-card">
          <CardHeader className="pb-4">
            <div className="flex gap-2">
              <Button
                variant={mode === 'login' ? 'default' : 'ghost'}
                className={mode === 'login' ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}
                onClick={() => setMode('login')}
                data-testid="login-tab"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Sign In
              </Button>
              <Button
                variant={mode === 'register' ? 'default' : 'ghost'}
                className={mode === 'register' ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}
                onClick={() => setMode('register')}
                data-testid="register-tab"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Register
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'register' && (
                <div>
                  <Label>Full Name</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="John Smith"
                    required
                    data-testid="engineer-name-input"
                  />
                </div>
              )}
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="engineer@helioses.co.uk"
                  required
                  data-testid="engineer-email-input"
                />
              </div>
              <div>
                <Label>Password</Label>
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Enter your password"
                  required
                  data-testid="engineer-password-input"
                />
              </div>
              {mode === 'register' && (
                <div>
                  <Label>Phone</Label>
                  <Input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="07xxxxxxxxx"
                    data-testid="engineer-phone-input"
                  />
                </div>
              )}
              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                disabled={loading}
                data-testid="engineer-submit-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {mode === 'login' ? 'Sign In' : 'Register'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-slate-500 dark:text-slate-400 mt-6">
          <Link to="/login" className="text-blue-600 hover:underline">Back to Admin Login</Link>
        </p>
      </div>
    </div>
  );
};
